import { formValueSelector } from 'redux-form'

import { track, MIXPANEL_EVENT } from 'ggx-service/mixpanel/mixpanel'
import { appStore } from '../../store'
import { orderTimeService } from './order-time.service'
import { getDeliveryDropoffTime } from '../duck/selectors'

const orderTrackingService = {
  /**
   * @see https://docs.google.com/spreadsheets/d/1gifSvB_KluUbOLDU1wQTkJLJsFwRyAJQu9RBt-Vmhj0/edit#gid=419090439&range=B77:B94
   */
  trackPlaceOrderC2: ({ quotation = {}, paymentMethod = 'none' } = {}) => {
    const eventName = MIXPANEL_EVENT.DELIVERY.PLACE_ORDER
    const state = appStore.getState()
    const setNullFallback = value =>
      typeof value === 'undefined' || value === '' ? null : value
    const selectFromState = field =>
      setNullFallback(formValueSelector('delivery-order')(state, field))
    const priceBreakdown = quotation.breakdown || []

    const hasSurcharge = priceBreakdown.some(
      breakdown => breakdown.type === 'speed_surcharge'
    )
    const hasNightcharge = priceBreakdown.some(
      breakdown => breakdown.type === 'night_surcharge'
    )
    const {
      request_at: pickupTime,
      drop_off_at: dropoffTime,
    } = orderTimeService.getUpdatedPickupDropoffTime({
      requestAt: selectFromState('request_at'),
      dropOffAt: selectFromState('drop_off_at'),
    })
    const packageSize = setNullFallback(
      [
        selectFromState('length'),
        selectFromState('width'),
        selectFromState('height'),
      ]
        .filter(dimension => dimension !== null)
        .join('x')
    )

    const properties = {
      shipper_name: selectFromState('shipper_attributes.name'),
      shipper_contact_number: selectFromState(
        'shipper_attributes.phone_number'
      ),
      shipper_address: selectFromState('shipper_attributes.address'),
      shipper_floor_room: selectFromState('shipper_attributes.details'),
      recipient_name: selectFromState('recipient_attributes.name'),
      recipient_contact_number: selectFromState(
        'recipient_attributes.phone_number'
      ),
      recipient_address: selectFromState('recipient_attributes.address'),
      recipient_floor_room: selectFromState('recipient_attributes.details'),

      package_content: selectFromState('info'),
      package_size: packageSize,
      weight: selectFromState('weight'),
      remarks: selectFromState('remark'),
      image_attached: !!selectFromState('image_secret'),
      pickup_time: pickupTime,
      drop_off_time: dropoffTime,

      has_surcharge: hasSurcharge,
      has_nightcharge: hasNightcharge,
      pay_with: paymentMethod,
    }

    track(eventName, properties)
  },

  /**
   * @see https://docs.google.com/spreadsheets/d/1gifSvB_KluUbOLDU1wQTkJLJsFwRyAJQu9RBt-Vmhj0/edit#gid=419090439&range=B77:B94
   */
  trackPlaceOrder: ({ quotation = {}, tid } = {}) => {
    const eventName = MIXPANEL_EVENT.DELIVERY.ORDER_CONFIRMED
    const state = appStore.getState()
    const selectFromState = field =>
      formValueSelector('delivery-order')(state, field)
    const quotationType = selectFromState('delivery_quotation_type')

    const pickupTime = selectFromState('pickup_time')
    const {
      request_at: convertNowToPickupTime,
    } = orderTimeService.getUpdatedPickupDropoffTime({ requestAt: pickupTime })
    const dropoffTime = getDeliveryDropoffTime(state)?.[quotationType]

    const properties = {
      shipper_name: selectFromState('shipper.name'),
      shipper_contact_number: selectFromState('shipper.phone_number'),
      shipper_address: selectFromState('shipper.address'),
      shipper_floor_room: selectFromState('shipper.details'),
      recipient_name: selectFromState('recipient.name'),
      recipient_contact_number: selectFromState('recipient.phone_number'),
      recipient_address: selectFromState('recipient.address'),
      recipient_floor_room: selectFromState('recipient.details'),

      package_content: selectFromState('info'),
      package_length: selectFromState('length'),
      package_width: selectFromState('width'),
      package_height: selectFromState('height'),
      package_weight:
        selectFromState('weight') && parseInt(selectFromState('weight'), 10),
      remarks: selectFromState('remark'),
      merchant_order_number: selectFromState('merchant_order_number'),
      image_attached: !!selectFromState('image.secret'),
      pickup_time: convertNowToPickupTime,
      dropoff_time: dropoffTime,
      dropoff_type: quotationType,

      payment_method: selectFromState('payment_type'),
      estimated_fee: quotation.total,
      order_request_tracking_id: tid,
    }

    track(eventName, properties)
  },

  trackPlaceOrderFormEvents: ({
    quotation = { total: 0 },
    formName = '',
  } = {}) => {
    const formNameMapping = {
      'pickup-form': 'Address_Input',
      'order-details-form': 'Package_Information',
      'schedule-form': 'Schedule_Order',
    }
    const eventName = MIXPANEL_EVENT.DELIVERY.FORM[formNameMapping[formName].toUpperCase()] 
    const state = appStore.getState()
    const selectFromState = field =>
      formValueSelector('delivery-order')(state, field)
    const quotationType = selectFromState('delivery_quotation_type')

    const pickupTime = selectFromState('pickup_time')
    const {
      request_at: convertNowToPickupTime,
    } = orderTimeService.getUpdatedPickupDropoffTime({ requestAt: pickupTime })
    const dropoffTime = getDeliveryDropoffTime(state)?.[quotationType]

    // put compulsory field values into one object to ensure user finish each section
    const addressInputForm = {
      shipper_name: selectFromState('shipper.name'),
      shipper_contact_number: selectFromState('shipper.phone_number'),
      shipper_address: selectFromState('shipper.address'),
      recipient_name: selectFromState('recipient.name'),
      recipient_contact_number: selectFromState('recipient.phone_number'),
      recipient_address: selectFromState('recipient.address'),
    }

    const orderDetailsForm = {
      package_content: selectFromState('info'),
      package_length: selectFromState('length'),
      package_width: selectFromState('width'),
      package_height: selectFromState('height'),
      package_weight: selectFromState('weight'),
      image_attached: !!selectFromState('image.secret'),
    }

    const scheduleForm = {
      pickup_time: convertNowToPickupTime,
      dropoff_time: dropoffTime,
      dropoff_type: quotationType,
    }

    const optionalFields = {
      shipper_floor_room: selectFromState('shipper.details'),
      recipient_floor_room: selectFromState('recipient.details'),
      remarks: selectFromState('remark'),
      merchant_order_number: selectFromState('merchant_order_number'),
    }

    const addressInputFormHasEmpty =
      Object.values(addressInputForm).includes(undefined) &&
      formNameMapping['pickup-form']
    const orderDetailsFormHasEmpty =
      Object.values(orderDetailsForm).includes(undefined) &&
      formNameMapping['order-details-form']
    const scheduleFormHasEmpty =
      Object.values(scheduleForm).includes(undefined) &&
      formNameMapping['schedule-form']

    const properties = {
      ...addressInputForm,
      ...orderDetailsForm,
      ...scheduleForm,
      ...optionalFields,
      payment_method: selectFromState('payment_type'),
      forms_currently_empty: [
        addressInputFormHasEmpty,
        orderDetailsFormHasEmpty,
        scheduleFormHasEmpty,
      ].filter(Boolean),
      estimated_fee: quotation.total === 0 ? undefined : quotation.total,
    }

    track(eventName, properties)
  },

  trackTextLinkClicked: ({ url, urlName }) => {
    const eventName = MIXPANEL_EVENT.DELIVERY.PAGE_LINK_CLICKED

    const properties = {
      url,
      url_name: urlName,
    }

    track(eventName, properties)
  },

  trackPackagePhotoUpload: ({
    imageUploaded,
    uploadType,
    uploadError,
  } = {}) => {
    const eventName = MIXPANEL_EVENT.DELIVERY.FORM.PACKAGE_PHOTO_UPLOAD
    const uploadMethod = {
      drop: 'drag_and_drop',
      change: 'choose_to_upload',
      populate: 'populate',
    }
    const properties = {
      image_attached: imageUploaded,
      image_upload_type: uploadMethod[uploadType],
      image_upload_error: uploadError,
    }

    track(eventName, properties)
  },

  trackPlaceOrderPage: () => {
    track(MIXPANEL_EVENT.DELIVERY.LANDING)
  },

  trackDropOffTimeSelected: ({ quotation, dropOffOption }) => {
    const eventName = MIXPANEL_EVENT.DELIVERY.FORM.DROPOFF_TIME_SELECTED

    const properties = {
      estimated_fee: quotation,
      dropoff_time: dropOffOption,
    }

    track(eventName, properties)
  },
}

export { orderTrackingService }
